import { TOUR_TARGETS, tourTargetSelector } from "./tourTargets";

export const MY_WORK_TOUR_KEY = "my-work-basics";
export const INBOX_TOUR_KEY = "inbox-basics";
export const LCC_TOUR_KEY = "lead-command-centre-basics";
export const XPLOREBYTE_BASICS_TOUR_KEY = "xplorebyte-basics";

export const TOUR_REGISTRY = Object.freeze({
  [MY_WORK_TOUR_KEY]: Object.freeze({
    title: "My Work basics",
    experienceKey: MY_WORK_TOUR_KEY,
    experienceType: "Tour",
    version: 1,
    forceReplayRoutes: Object.freeze(["/app/crm/my-work"]),
    steps: Object.freeze([
      {
        element: tourTargetSelector(TOUR_TARGETS.MY_WORK.HEADER),
        popover: {
          title: "Your daily work centre",
          description:
            "My Work brings together tasks, follow-ups, scheduled messages, and customer actions that need attention.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.MY_WORK.SUMMARY),
        popover: {
          title: "See what needs attention",
          description:
            "Use these summaries to spot overdue work, today’s commitments, completed items, and delivery issues.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.MY_WORK.TABS),
        popover: {
          title: "Choose the right view",
          description:
            "Move between today, upcoming work, scheduled messages, completed work, and any additional views available to you.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.MY_WORK.FILTERS),
        popover: {
          title: "Narrow the workload",
          description:
            "Filter by type, priority, or status, and search for a work item or contact without changing the underlying work.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.MY_WORK.ACTIONS),
        popover: {
          title: "Create work when permitted",
          description:
            "Use the actions available to your role to add a task or schedule a WhatsApp follow-up.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.MY_WORK.LIST),
        popover: {
          title: "Work from the list",
          description:
            "Open an item for context, then use its permitted actions to move the customer commitment forward.",
        },
      },
    ]),
  }),
  [INBOX_TOUR_KEY]: Object.freeze({
    title: "Inbox basics",
    experienceKey: INBOX_TOUR_KEY,
    experienceType: "Tour",
    version: 1,
    forceReplayRoutes: Object.freeze(["/app/inbox/chatinbox"]),
    options: Object.freeze({ stagePadding: 0 }),
    steps: Object.freeze([
      {
        element: tourTargetSelector(TOUR_TARGETS.INBOX.SHELL),
        popover: {
          title: "Your conversation workspace",
          description:
            "Inbox brings the customer queue, conversation history, reply tools, and CRM context into one workspace.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.INBOX.CONVERSATIONS),
        popover: {
          title: "Prioritize the queue",
          description:
            "Use the available views, filters, and search to find the conversations that need attention.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.INBOX.CONVERSATION_HEADER),
        popover: {
          title: "Understand the active conversation",
          description:
            "The header identifies the customer and shows the current ownership and conversation status controls available to you.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.INBOX.MESSAGES),
        popover: {
          title: "Review the conversation",
          description:
            "Read the message history and delivery context before deciding how to respond.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.INBOX.COMPOSER),
        popover: {
          title: "Choose the right reply",
          description:
            "When your role, ownership, and messaging window allow it, reply with the appropriate message or approved template.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.INBOX.DETAILS),
        popover: {
          title: "Keep customer context nearby",
          description:
            "Use Contact Details to review relevant context without leaving the conversation workspace.",
        },
      },
    ]),
  }),
  [LCC_TOUR_KEY]: Object.freeze({
    title: "Lead Command Centre basics",
    experienceKey: LCC_TOUR_KEY,
    experienceType: "Tour",
    version: 1,
    forceReplayRoutes: Object.freeze(["/app/dashboard/lead-command-centre"]),
    steps: Object.freeze([
      {
        element: tourTargetSelector(TOUR_TARGETS.LCC.HEADER),
        popover: {
          title: "Your sales command centre",
          description:
            "Use Lead Command Centre to focus the sales queue, understand lead context, and choose the next action.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.LCC.VIEW_MODE),
        popover: {
          title: "Choose Contact or Enquiry view",
          description:
            "Contact View summarizes each lead; Enquiry View separates individual enquiry journeys when that detail matters.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.LCC.METRICS),
        popover: {
          title: "Prioritize with live queues",
          description:
            "Use the KPI queues to focus on hot, unassigned, stale, or response-due leads.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.LCC.FILTERS),
        popover: {
          title: "Narrow the sales queue",
          description:
            "Combine dates, ownership, stages, saved views, and available lead-flow filters, then apply them to the results.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.LCC.VIEW_CONTROLS),
        popover: {
          title: "Shape your working view",
          description:
            "Choose which filters and columns are useful to you; additional controls appear only when your configuration permits them.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.LCC.RESULTS),
        popover: {
          title: "Work from the results",
          description:
            "Review lead context in the table and use only the actions available to your role and current workflow.",
        },
      },
    ]),
  }),
  [XPLOREBYTE_BASICS_TOUR_KEY]: Object.freeze({
    title: "Xplorebyte Basics",
    experienceKey: XPLOREBYTE_BASICS_TOUR_KEY,
    experienceType: "Tour",
    version: 1,
    forceReplayRoutes: Object.freeze(["/app/*"]),
    steps: Object.freeze([
      {
        element: tourTargetSelector(TOUR_TARGETS.NAV.INBOX),
        popover: {
          title: "Inbox",
          description:
            "Handle customer conversations, review message history, and respond when your role and conversation state allow it.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.NAV.MY_WORK),
        popover: {
          title: "My Work",
          description:
            "Keep track of tasks, follow-ups, scheduled messages, and commitments that need attention.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.NAV.LEAD_COMMAND_CENTRE),
        popover: {
          title: "Lead Command Centre",
          description:
            "Prioritize the sales queue with lead intelligence, filters, and role-appropriate actions.",
        },
      },
      {
        element: tourTargetSelector(TOUR_TARGETS.NAV.HELP),
        popover: {
          title: "Help is always available",
          description:
            "Open Help & Support whenever you want documentation or to replay a Quick Tour.",
        },
      },
    ]),
  }),
});

export function getTourDefinition(tourKey, registry = TOUR_REGISTRY) {
  if (!tourKey || !Object.prototype.hasOwnProperty.call(registry, tourKey)) {
    return null;
  }

  return registry[tourKey];
}
